import 'intersection-observer';
import { Directive, DirectiveBinding } from 'vue';
import { getScreenSize } from '@utils/client';

const elementSrcMap = new WeakMap<HTMLElement, { value: string; loaded: boolean }>();

function loadImage(src: string, target: Element, el: HTMLElement): void {
	const image = new Image();
	if (target.tagName !== 'IMG') {
		target.setAttribute('style', `background-image: url('${src}');`);
	} else {
		target.setAttribute('src', src);
	}

	image.src = src;

	image.onload = function (): void {
		el.classList.add('in');
	};
}

function determineCorrectSrcAndLoadImage(src: string, target: Element, el: HTMLElement): void {
	const sources = src.split(',');
	const sourceParsed = {
		sm: '',
		md: '',
		lg: '',
		default: ''
	};

	sources.forEach((source) => {
		source = source.trim();

		const lastThreeLetters = source.slice(-3);
		if (lastThreeLetters.indexOf('_sm') >= 0) {
			sourceParsed.sm = source.slice(0, -3);
		} else if (lastThreeLetters.indexOf('_md') >= 0) {
			sourceParsed.md = source.slice(0, -3);
		} else if (lastThreeLetters.indexOf('_lg') >= 0) {
			sourceParsed.lg = source.slice(0, -3);
		} else {
			sourceParsed.default = source;
		}
	});

	if (getScreenSize() === 'MOBILE') {
		loadImage(sourceParsed.sm || sourceParsed.default, target, el);
	} else if (getScreenSize() === 'DESKTOP') {
		loadImage(sourceParsed.lg || sourceParsed.md || sourceParsed.default, target, el);
	} else {
		loadImage(sourceParsed.md || sourceParsed.default, target, el);
	}
}

function loadSrc(src: string, el: HTMLElement): void {
	if (src.indexOf(',') === -1) {
		loadImage(src, el, el);
	} else {
		determineCorrectSrcAndLoadImage(src, el, el);
	}
}

export const lazySrc: Directive = {
	beforeMount: function (el: HTMLElement, binding: DirectiveBinding) {
		let observer: IntersectionObserver | null = null;
		const srcRef = { value: binding.value, loaded: false };
		elementSrcMap.set(el, srcRef);

		function loadOnIntersect(entries: Array<IntersectionObserverEntry>): void {
			entries.forEach((entry) => {
				if (observer && entry.isIntersecting) {
					srcRef.loaded = true;
					loadSrc(srcRef.value, el);
					observer.unobserve(entry.target);
				}
			});
		}

		if (!Array.from(el.classList).some((className) => className.startsWith('fade-'))) {
			el.classList.add('fade-in-active');
		}

		observer = new IntersectionObserver(loadOnIntersect, {
			threshold: 0,
			rootMargin: '300px'
		});
		observer.observe(el);
	},
	updated: function (el: HTMLElement, binding: DirectiveBinding) {
		if (binding.value !== binding.oldValue) {
			const srcRef = elementSrcMap.get(el);
			if (srcRef) {
				srcRef.value = binding.value;
				el.classList.remove('in');
				if (!Array.from(el.classList).some((className) => className.startsWith('fade-'))) {
					el.classList.add('fade-in-active');
				}

				if (srcRef.loaded) {
					loadSrc(binding.value, el);
				}
			}
		}
	}
};
export default lazySrc;
